import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetDevicesQuery } from "store/api/devicesApi"
import {
  useLazyGetPerformanceTestReportQuery,
  useLazyGetPerformanceTestsHistoryQuery,
} from "store/api/performanceTestApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showPerformanceTestReportModal } from "store/modalSlise"
import { setPerformanceTestReport, setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { ApiTypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDataToPerformanceTestHistoryTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { PerformanceTestActionsModal } from "modals/PerformanceTestActionsModal"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"

import { useCustomerDashboard } from "../CustomerDashboardContext"
import { StyledTableNavWrapper, StyledTableWrapper } from "../styled"
import { customerDashboardPerformanceTestTableHeaderCells } from "./customerDashboardPerformanceTestTableHeaderCells"
import {
  dummyCustomerDashboardPerfTestHistory,
  dummyCustomerDashboardPerfTestMeta,
} from "./dummyCustomerDashboardPerfTestData"
import { UpcomingScheduleCard } from "./UpcomingScheduleCard"

const PerformanceTestContainer = styled.div<{ sidebarOpen?: boolean }>`
  margin-top: 2px;
  padding: 0 ${({ sidebarOpen }) => (sidebarOpen ? "24px" : "0")} 20px 0;
  max-height: calc(100dvh - 255px);
  overflow-y: auto;
`

const SectionHeading = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: #0f172a;
  margin: 0 0 16px 0;
`

export const CustomerDashboardPerformanceTest = () => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const { devices, currentDevice, isSidebarOpen } = useCustomerDashboard()
  const [triggerGetPerformanceTestsHistory, { isFetching, data }] = useLazyGetPerformanceTestsHistoryQuery()
  const [triggerGetDevice] = useLazyGetDevicesQuery()
  const [triggerGetPerformanceTestReport] = useLazyGetPerformanceTestReportQuery()
  const [showPerformanceTestModal, setShowPerformanceTestModal] = useState(false)
  const { urlQueryParams } = useUrlQueryParams()

  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false

  const selectedDevice = devices[currentDevice]
  const deviceSN = selectedDevice?.sn

  const sendRequest = useCallback(async () => {
    if (!deviceSN || !hasPerformanceTestAccess) return
    try {
      const requestParams = { ...urlQueryParams, sn: deviceSN }
      const res = await triggerGetPerformanceTestsHistory(requestParams)
      const error = getErrorMessage(res?.error)
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }, [triggerGetPerformanceTestsHistory, urlQueryParams, deviceSN, hasPerformanceTestAccess])

  useEffect(() => {
    sendRequest()
  }, [urlQueryParams, deviceSN, sendRequest, hasPerformanceTestAccess])

  const handleStartPerformanceTest = () => {
    if (!selectedDevice) return
    const performanceTestDevice: ApiTypes.Model.PerformanceTestDevice = {
      id: selectedDevice.id,
      email: selectedDevice.email || "",
      sn: selectedDevice.sn,
      system_type: selectedDevice.system_type || "",
      status:
        (selectedDevice.perftest?.status?.toLowerCase() as ApiTypes.Model.PerformanceTestDevice["status"]) ||
        "scheduled",
    }
    dispatch(setSelectedDevicesForPerformanceTest({ devices: [performanceTestDevice], isImmediate: false }))
    setShowPerformanceTestModal(true)
  }

  const handlePerformanceTestSuccess = useCallback(() => {
    setShowPerformanceTestModal(false)
    sendRequest()
  }, [sendRequest])

  const handlePerformanceTestCancel = () => {
    setShowPerformanceTestModal(false)
    dispatch(setSelectedDevicesForPerformanceTest({ devices: [], isImmediate: false }))
  }

  const handleTableRowClick = async (id: number) => {
    const performanceTestHistory = data?.data || []
    const currentTest = performanceTestHistory.find(item => item.perftest.perftest_result_id === id)
    if (!currentTest) return

    const deviceData = await triggerGetDevice({ sn: currentTest?.device?.sn })

    if (deviceData.error) {
      toast.error("Something went wrong, please try again")
      return
    }

    const device = deviceData.data?.data[0]
    const res = await triggerGetPerformanceTestReport(id)

    if (res.data && device) {
      dispatch(
        setPerformanceTestReport({
          ...res?.data,
          customer: device.homeowner_name,
          zip_code: device.zip?.code,
          where_installed_name: currentTest?.device?.where_installed_name,
          timeZone: device.time_zone_text,
        }),
      )
      dispatch(showPerformanceTestReportModal(true))
    }
  }

  const performanceTestHistory = hasPerformanceTestAccess ? data?.data || [] : dummyCustomerDashboardPerfTestHistory
  const meta = hasPerformanceTestAccess ? data?.meta : dummyCustomerDashboardPerfTestMeta

  return (
    <PerformanceTestContainer sidebarOpen={isSidebarOpen}>
      <StyledTableNavWrapper>
        <ButtonNew onClick={handleStartPerformanceTest} size="auto" color="blue">
          Start Performance Test
        </ButtonNew>
      </StyledTableNavWrapper>
      <SectionHeading>Upcoming Schedule</SectionHeading>
      <UpcomingScheduleCard />
      <SectionHeading>Test History</SectionHeading>
      <StyledTableWrapper
        headerCells={customerDashboardPerformanceTestTableHeaderCells}
        columnsLoaded={true}
        data={convertDataToPerformanceTestHistoryTableRows(performanceTestHistory)}
        isLoading={isFetching}
        isMobileDevice={isMobileDevice()}
        fitContainer={true}
        onRowClick={handleTableRowClick}
      />
      {meta && meta.last_page > 1 && <Pagination meta={meta} />}
      {showPerformanceTestModal && (
        <PerformanceTestActionsModal onCancel={handlePerformanceTestCancel} onSuccess={handlePerformanceTestSuccess} />
      )}
      {!hasPerformanceTestAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.PerformanceTest} />}
    </PerformanceTestContainer>
  )
}
